<template lang="pug">
.course-info-editor
  LoadingSpinner(v-if='fetchingCourse')
  template(v-else)
    CreateOrUpdateCourse(
      :editor-title='String($t("editor.createCourse.updateCourse"))',
      :show-update-content-c-t-a='!fetchingCourseModules',
      @has-course-changes='hasCourseChanges = $event',
      @update-training-content='createOrViewModule',
      update-course
    )
</template>

<script setup lang="ts">
  import { computed, ref } from 'vue'

  import LoadingSpinner from '@/components/common/LoadingSpinner.vue'
  import CreateOrUpdateCourse from '@/components/editor/CreateOrUpdateCourse.vue'

  import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router/composables'
  import useCourse from '@/composables/useCourse'
  import useEditor from '@/composables/useEditor'
  import useI18n from '@/composables/useI18n'

  import { CourseModule } from '@/store/modules/course'
  import { EditorModule } from '@/store/modules/editor'

  import eventBus from '@/main'

  import { v4 as uuidv4 } from 'uuid'

  defineEmits(['has-course-changes', 'update-training-content'])

  const route = useRoute()
  const router = useRouter()
  const { course, currentCourseId, courseModules, routeToFirstCourseModule } = useCourse()

  const hasCourseChanges = ref(false)

  const fetchingCourse = computed(() => CourseModule.course === null || !EditorModule.editMode)

  const fetchingCourseModules = computed(() => CourseModule.coursesModuleMap[currentCourseId.value] === undefined)

  const createOrViewModule = () => {
    if (courseModules.value?.length) return routeToFirstCourseModule(currentCourseId.value)
    createModule()
  }

  const createModule = () => {
    router
      .push({
        name: 'CourseModule',
        params: { courseId: currentCourseId.value, moduleId: 'mod-' + uuidv4() },
      })
      .catch(() => {
        return
      })
  }

  const setSubmitAllCourseChanges = () => {
    EditorModule.setSubmitCourseChanges(true)
  }

  const setDiscardAllCourseChanges = () => {
    EditorModule.setDiscardCourseChanges(true)
  }

  const getCourseInfo = () => {
    if (currentCourseId.value && !course.value) {
      CourseModule.getCourse({ courseId: currentCourseId.value }).then((fetchedCourse) => {
        if (fetchedCourse) {
          if (CourseModule.courses) {
            const existingCourse = CourseModule.courses.find((c) => c.id === currentCourseId.value)
            if (!existingCourse) {
              CourseModule.courses.unshift(fetchedCourse)
            }
          }
        }
      })
    }
  }

  getCourseInfo()

  if (fetchingCourseModules.value && currentCourseId.value) {
    CourseModule.getCourseModules(currentCourseId.value)
  }

  onBeforeRouteLeave((_to, _from, next) => {
    const { inEditorMode } = useEditor(route)
    const { translateString } = useI18n()

    if (!inEditorMode) {
      return next()
    }

    if (hasCourseChanges.value) {
      eventBus.$emit('show-modal', {
        modalContentComponent: 'ConfirmationModal',
        modalTitle: translateString('editor.thereAreChanges'),
        modalProps: {
          confirmationText: translateString('editor.saveOrDiscard'),
          cancelButtonText: translateString('editor.discardChanges'),
          okButtonText: translateString('editor.saveAllChanges'),
          showSvgs: true,
          okCallback: (closeCallback: () => void) => {
            setSubmitAllCourseChanges()
            // next()
            if (typeof closeCallback === 'function') closeCallback()
          },
          cancelCallback: () => {
            setDiscardAllCourseChanges()
            // next()
          },
        },
        cssClass: 'confirmation editor',
        modalCloseCallback: (callback: () => void) => {
          if (typeof callback === 'function') callback()
        },
      })
    } else {
      next()
    }
  })
</script>

<style lang="postcss">
  .course-info-editor {
    .host-description {
      .text-renderer {
        @apply xl:ketch-max-w-[500px];
      }
    }
    .state-and-feedback-email {
      .text-renderer {
        @apply xl:ketch-max-w-[250px];
      }
    }
    .course-cta-wrapper {
      @apply ketch-w-screen ketch-bg-white ketch-right-0 ketch-bottom-0 ketch-py-c10 ketch-shadow-[5px_0_0_rgb(0,0,0,10%)];
      .course-cta {
        @apply ketch-relative ketch-right-[150px] xl:ketch-right-[calc((100%-1060px)/2)];
      }
    }
  }
</style>
